import {
  QuerySettingsDto,
  RequestType,
  Timespan,
  DeviceSensor,
  DeviceSensorType,
  ApiDevicesTransmissionsMetric,
} from '@thingslog/repositories';

export class QuerySettingsFactory {
  public static create(props: MutateProps): QuerySettingsDto {
    switch (props.type) {
      case 'api-alarms-count':
        return {
          type: props.type,
          timespan: props.timespan,
        };
      case 'api-battery-histogram':
        return {
          type: props.type,
        };
      case 'api-device-sensor':
        return {
          type: props.type,
          deviceNumber: props.deviceNumber,
          sensorIndex: props.sensorIndex,
          timespan: props.timespan,
        };
      case 'api-devices-battery':
        return {
          type: props.type,
          specificDevices: props.specificDevices,
          fromLevel: props.fromLevel,
          toLevel: props.toLevel,
        };
      case 'api-devices-signal':
        return {
          type: props.type,
          specificDevices: props.specificDevices,
          fromLevel: props.fromLevel,
          toLevel: props.toLevel,
          metrics: props.metrics,
        };
      case 'api-devices-transmissions':
        return {
          type: props.type,
          metrics: props.metrics,
        };
      case 'api-signal-histogram':
        return {
          type: props.type,
        };
      case 'api-readings-device-groups':
        return {
          type: props.type,
          deviceGroupIds: props.deviceGroupIds,
          timespan: props.timespan,
          preferFlow: props.preferFlow,
        };
      case 'api-readings-device-sensor-list':
        return {
          type: props.type,
          deviceSensors: props.deviceSensors,
          timespan: props.timespan,
          preferFlow: props.preferFlow,
        };
      case 'api-readings-device-sensor-type-list':
        return {
          type: props.type,
          deviceSensorTypes: props.deviceSensorTypes,
          timespan: props.timespan,
          preferFlow: props.preferFlow,
        };
      case 'api-readings-mixed':
        return {
          type: props.type,
          deviceGroupIds: props.deviceGroupIds,
          deviceSensors: props.deviceSensors,
          deviceSensorTypes: props.deviceSensorTypes,
          timespan: props.timespan,
          preferFlow: props.preferFlow,
        };
      default:
        throw new Error('Invalid query settings type');
    }
  }

  public static update(
    querySettings: QuerySettingsDto,
    props: Partial<MutateProps>
  ): QuerySettingsDto {
    if (querySettings.type === 'api-alarms-count' && props.type === 'api-alarms-count') {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (querySettings.type === 'api-battery-histogram' && props.type === 'api-battery-histogram') {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (querySettings.type === 'api-device-sensor' && props.type === 'api-device-sensor') {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (querySettings.type === 'api-devices-battery' && props.type === 'api-devices-battery') {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (querySettings.type === 'api-devices-signal' && props.type === 'api-devices-signal') {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (
      querySettings.type === 'api-devices-transmissions' &&
      props.type === 'api-devices-transmissions'
    ) {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (querySettings.type === 'api-signal-histogram' && props.type === 'api-signal-histogram') {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (
      querySettings.type === 'api-readings-device-groups' &&
      props.type === 'api-readings-device-groups'
    ) {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (
      querySettings.type === 'api-readings-device-sensor-list' &&
      props.type === 'api-readings-device-sensor-list'
    ) {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (
      querySettings.type === 'api-readings-device-sensor-type-list' &&
      props.type === 'api-readings-device-sensor-type-list'
    ) {
      return {
        ...querySettings,
        ...props,
      };
    }
    if (querySettings.type === 'api-readings-mixed' && props.type === 'api-readings-mixed') {
      return {
        ...querySettings,
        ...props,
      };
    }
    throw new Error('Invalid query settings type');
  }
}

interface MutatePropBase {
  type: RequestType;
}

interface MutatePropsApiAlarmsCount extends MutatePropBase {
  type: 'api-alarms-count';
  timespan: Timespan | null;
}

interface MutatePropsApiBatteryHistogram extends MutatePropBase {
  type: 'api-battery-histogram';
}

interface MutatePropsApiDeviceSensor extends MutatePropBase {
  type: 'api-device-sensor';
  deviceNumber: string;
  sensorIndex: string;
  timespan: Timespan | null;
}

interface MutatePropsApiDevicesBattery extends MutatePropBase {
  type: 'api-devices-battery';
  specificDevices: string[];
  fromLevel: string;
  toLevel: string;
}

interface MutatePropsApiDevicesSignal extends MutatePropBase {
  type: 'api-devices-signal';
  specificDevices: string[];
  fromLevel: string;
  toLevel: string;
  metrics: ('level' | 'rssi')[];
}

interface MutatePropsApiDevicesTransmissions extends MutatePropBase {
  type: 'api-devices-transmissions';
  metrics: ApiDevicesTransmissionsMetric[];
}

interface MutatePropsApiSignalHistogram extends MutatePropBase {
  type: 'api-signal-histogram';
}

interface MutatePropsApiReadingsDeviceGroups extends MutatePropBase {
  type: 'api-readings-device-groups';
  deviceGroupIds: string[];
  timespan: Timespan | null;
  preferFlow: boolean;
}

interface MutatePropsApiReadingsDeviceSensorList extends MutatePropBase {
  type: 'api-readings-device-sensor-list';
  deviceSensors: DeviceSensor[];
  timespan: Timespan | null;
  preferFlow: boolean;
}

interface MutatePropsApiReadingsDeviceSensorTypeList extends MutatePropBase {
  type: 'api-readings-device-sensor-type-list';
  deviceSensorTypes: DeviceSensorType[];
  timespan: Timespan | null;
  preferFlow: boolean;
}

interface MutatePropsApiReadingsMixed extends MutatePropBase {
  type: 'api-readings-mixed';
  deviceGroupIds: string[];
  deviceSensors: DeviceSensor[];
  deviceSensorTypes: DeviceSensorType[];
  timespan: Timespan | null;
  preferFlow: boolean;
}

type MutateProps =
  | MutatePropsApiAlarmsCount
  | MutatePropsApiBatteryHistogram
  | MutatePropsApiDeviceSensor
  | MutatePropsApiDevicesBattery
  | MutatePropsApiDevicesSignal
  | MutatePropsApiDevicesTransmissions
  | MutatePropsApiSignalHistogram
  | MutatePropsApiReadingsDeviceGroups
  | MutatePropsApiReadingsDeviceSensorList
  | MutatePropsApiReadingsDeviceSensorTypeList
  | MutatePropsApiReadingsMixed;
